import { supabase } from '../lib/supabase';

const escalationService = {
  /**
   * Trigger the edge function to check for 24h+ unanswered inquiries
   */
  async triggerEscalationCheck() {
    try {
      const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
      const { data: { session } } = await supabase?.auth?.getSession();

      const headers = { 'Content-Type': 'application/json' };
      if (session?.access_token) {
        headers['Authorization'] = `Bearer ${session?.access_token}`;
      }

      const response = await fetch(
        `${supabaseUrl}/functions/v1/escalate-unanswered-inquiries`,
        { method: 'POST', headers }
      );

      if (!response?.ok) {
        const err = await response?.json()?.catch(() => ({}));
        return { data: null, error: err?.error || 'Escalation check failed' };
      }

      const data = await response?.json();
      return { data, error: null };
    } catch (error) {
      console.error('[EscalationService] triggerEscalationCheck error:', error);
      return { data: null, error: error?.message };
    }
  },

  /**
   * Fetch all urgent professional bookings
   */
  async getUrgentBookings() {
    try {
      const { data, error } = await supabase
        ?.from('professional_bookings')
        ?.select('*')
        ?.eq('is_urgent', true)
        ?.not('booking_status', 'in', '("cancelled","completed")')
        ?.order('escalated_at', { ascending: true });

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      console.error('[EscalationService] getUrgentBookings error:', error);
      return { data: [], error: error?.message };
    }
  },

  /**
   * Fetch all urgent property inquiries
   */
  async getUrgentInquiries() {
    try {
      const { data, error } = await supabase
        ?.from('property_inquiries')
        ?.select(`
          *,
          property:properties(id, title, address)
        `)
        ?.eq('is_urgent', true)
        ?.not('status', 'in', '("resolved","closed")')
        ?.order('escalated_at', { ascending: true });

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      console.error('[EscalationService] getUrgentInquiries error:', error);
      return { data: [], error: error?.message };
    }
  },

  /**
   * Get escalation logs for admin audit trail
   */
  async getEscalationLogs(limit = 50) {
    try {
      const { data, error } = await supabase
        ?.from('escalation_logs')
        ?.select('*')
        ?.order('escalated_at', { ascending: false })
        ?.limit(limit);

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      console.error('[EscalationService] getEscalationLogs error:', error);
      return { data: [], error: error?.message };
    }
  },

  /**
   * Resolve an escalated booking (mark as responded)
   */
  async resolveBookingEscalation(bookingId, note = '') {
    try {
      const now = new Date()?.toISOString();

      const { data, error } = await supabase
        ?.from('professional_bookings')
        ?.update({
          is_urgent: false,
          professional_responded_at: now,
          booking_status: 'confirmed',
        })
        ?.eq('id', bookingId)
        ?.select()
        ?.single();

      if (error) throw error;

      // Update escalation log
      await supabase
        ?.from('escalation_logs')
        ?.update({ resolved_at: now, resolution_note: note })
        ?.eq('entity_type', 'professional_booking')
        ?.eq('entity_id', bookingId)
        ?.is('resolved_at', null);

      return { data, error: null };
    } catch (error) {
      console.error('[EscalationService] resolveBookingEscalation error:', error);
      return { data: null, error: error?.message };
    }
  },

  /**
   * Resolve an escalated inquiry (mark as responded)
   */
  async resolveInquiryEscalation(inquiryId, note = '') {
    try {
      const now = new Date()?.toISOString();

      const { data, error } = await supabase
        ?.from('property_inquiries')
        ?.update({
          is_urgent: false,
          professional_responded_at: now,
          status: 'responded',
        })
        ?.eq('id', inquiryId)
        ?.select()
        ?.single();

      if (error) throw error;

      // Update escalation log
      await supabase
        ?.from('escalation_logs')
        ?.update({ resolved_at: now, resolution_note: note })
        ?.eq('entity_type', 'property_inquiry')
        ?.eq('entity_id', inquiryId)
        ?.is('resolved_at', null);

      return { data, error: null };
    } catch (error) {
      console.error('[EscalationService] resolveInquiryEscalation error:', error);
      return { data: null, error: error?.message };
    }
  },

  /**
   * Get escalation stats for admin dashboard KPIs
   */
  async getEscalationStats() {
    try {
      const [urgentBookingsRes, urgentInquiriesRes, logsRes] = await Promise.all([
        supabase
          ?.from('professional_bookings')
          ?.select('*', { count: 'exact', head: true })
          ?.eq('is_urgent', true)
          ?.not('booking_status', 'in', '("cancelled","completed")'),
        supabase
          ?.from('property_inquiries')
          ?.select('*', { count: 'exact', head: true })
          ?.eq('is_urgent', true)
          ?.not('status', 'in', '("resolved","closed")'),
        supabase
          ?.from('escalation_logs')
          ?.select('*', { count: 'exact', head: true })
          ?.is('resolved_at', null),
      ]);

      return {
        data: {
          urgentBookings: urgentBookingsRes?.count || 0,
          urgentInquiries: urgentInquiriesRes?.count || 0,
          pendingResolution: logsRes?.count || 0,
          total: (urgentBookingsRes?.count || 0) + (urgentInquiriesRes?.count || 0),
        },
        error: null,
      };
    } catch (error) {
      console.error('[EscalationService] getEscalationStats error:', error);
      return { data: { urgentBookings: 0, urgentInquiries: 0, pendingResolution: 0, total: 0 }, error: error?.message };
    }
  },
};

export default escalationService;
